import React, { useEffect, useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Row, Container } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import dayjs from "dayjs";

import { reportOptions, formatOptions } from "./data.json";
import { getFormBaseValidationRule } from "../../utils/form/form-utils";
import EzControlledDropDowntWithFloatingLabel from "../../common/EzForm/EzControlledDropDown/WithFloatingLabel";
import EzControlledCalendarWithFloatingLabel from "../../common/EzForm/EzControlledCalendar/WithFloatingLabel";
import EzControlledRadioButtonWithLabel from "../../common/EzForm/EzControlledRadioButton/WithLabel";

const Reporting = React.memo(function Reporting({
  generateReport,
  customers = [],
  fetchFuelCustomers,
  isGenerateReportSuccess,
}) {
  const today = dayjs().toDate();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      transactions_from: today,
      transactions_to: today,
      date_from: today,
      date_to: today,
      currency: "usd",
      customerOption: "all",
    },
  });
  const selectedReport = watch("report");
  const [customerOption, setCustomerOption] = useState("all");

  useEffect(() => {
    fetchFuelCustomers();
  }, [fetchFuelCustomers]);

  useEffect(() => {
    if (isGenerateReportSuccess) reset();
  }, [isGenerateReportSuccess, reset]);

  const renderError = useCallback(
    (name) =>
      errors[name] ? (
        <small className="p-error">
          {errors[name]?.message || "This field is required."}
        </small>
      ) : null,
    [errors]
  );

  const handleCancel = useCallback(() => reset(), [reset]);

  const onSubmit = (data) => {
    if (data.report === "IFTA") {
      const selectedCompany = customers.find(
        (c) => c.company_id === data.companyId
      );
      const payload = {
        company_customer_id: selectedCompany.company_id,
        company_name: selectedCompany.name,
        currency: data.currency,
        date_from: dayjs(data.date_from).format("YYYY-MM-DD"),
        date_to: dayjs(data.date_to).format("YYYY-MM-DD"),
      };
      generateReport(payload);
    } else {
      const payload = {
        report: data.report,
        format: data.format,
        currency: String(data.currency),
        customerId: customerOption === "all" ? "ALL" : String(data.customerId),
        transactions_from: dayjs(data.transactions_from).format("YYYY-MM-DD"),
        transactions_to: dayjs(data.transactions_to).format("YYYY-MM-DD"),
      };
      generateReport(payload);
    }
  };

  // Define currency options
  const iftaCurrencyOptions = [
    { label: "USD", value: "usd" },
    { label: "CAD", value: "cad" },
  ];

  const currencyOptions = [
    { label: "USD", value: "USD" },
    { label: "CAD", value: "CAD" },
    { label: "ALL", value: "ALL" },
  ];

  const customerOptions = [
    { label: "All", value: "all" },
    { label: "Specific", value: "specific" },
  ];

  return (
    <div className="paddingbox">
      <div className="containerbox">
        <SectionHeader label="Reporting" />

        <form className="p-fluid" onSubmit={handleSubmit(onSubmit)}>
          <Container fluid>
            <div className="my-3 w-50">
              <EzControlledDropDowntWithFloatingLabel
                name="report"
                control={control}
                optionLabel="label"
                optionValue="value"
                options={reportOptions}
                label="Report"
                rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                errorMsg={renderError("report")}
                isError={!!errors.report}
              />
            </div>

            {selectedReport === "IFTA" ? (
              <>
                <EzControlledRadioButtonWithLabel
                  name="currency"
                  control={control}
                  rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                  sectionLabel="Currency"
                  options={iftaCurrencyOptions}
                  errorMsg={renderError("currency")}
                  isError={!!errors.currency}
                />
                <div className="my-3 w-50">
                  <EzControlledDropDowntWithFloatingLabel
                    name="companyId"
                    control={control}
                    optionLabel="name"
                    optionValue="company_id"
                    options={customers}
                    label="Company Name"
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                    errorMsg={renderError("companyId")}
                    isError={!!errors.companyId}
                  />
                </div>
                <div className="my-3 w-50">
                  <EzControlledCalendarWithFloatingLabel
                    name="date_from"
                    control={control}
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                    isError={!!errors.date_from}
                    errorMsg={renderError("date_from")}
                    label="Date From"
                  />
                </div>
                <div className="my-3 w-50">
                  <EzControlledCalendarWithFloatingLabel
                    name="date_to"
                    control={control}
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                    isError={!!errors.date_to}
                    errorMsg={renderError("date_to")}
                    label="Date To"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="my-3 w-50">
                  <EzControlledDropDowntWithFloatingLabel
                    name="format"
                    control={control}
                    optionLabel="label"
                    optionValue="value"
                    options={formatOptions}
                    label="Format"
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                    errorMsg={renderError("format")}
                    isError={!!errors.format}
                  />
                </div>
                <div className="my-3 w-50">
                  <EzControlledCalendarWithFloatingLabel
                    name="transactions_from"
                    control={control}
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                    isError={!!errors.transactions_from}
                    errorMsg={renderError("transactions_from")}
                    label="Transaction From"
                  />
                </div>
                <div className="my-3 w-50">
                  <EzControlledCalendarWithFloatingLabel
                    name="transactions_to"
                    control={control}
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                    isError={!!errors.transactions_to}
                    errorMsg={renderError("transactions_to")}
                    label="Transaction To"
                  />
                </div>
                <EzControlledRadioButtonWithLabel
                  name="currency"
                  control={control}
                  rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                  sectionLabel="Currency"
                  options={currencyOptions}
                  errorMsg={renderError("currency")}
                  isError={!!errors.currency}
                />
                <EzControlledRadioButtonWithLabel
                  name="customerOption"
                  control={control}
                  rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                  sectionLabel="Customer Name"
                  options={customerOptions}
                  onChange={(e) => {
                    setCustomerOption(e.value);
                    if (e.value === "all") {
                      setValue("customerId", "");
                    }
                  }}
                  errorMsg={
                    customerOption === "specific" && errors.customerId
                      ? renderError("customerId")
                      : null
                  }
                  isError={customerOption === "specific" && !!errors.customerId}
                />
                <div className="my-3 w-50">
                  <EzControlledDropDowntWithFloatingLabel
                    name="customerId"
                    control={control}
                    optionLabel="name"
                    optionValue="id"
                    options={customers}
                    label=""
                    errorMsg={null}
                    isError={!!errors.customerId}
                    disabled={customerOption !== "specific"}
                  />
                </div>
              </>
            )}
          </Container>

          <div className="d-flex justify-content-end mt-3">
            <Button
              label="Reset"
              type="button"
              className="p-btn-danger p-button-outlined mr-4"
              onClick={handleCancel}
              style={{ width: "100px" }}
            />
            <Button
              type="submit"
              label="Generate"
              className="p-button-raised"
              style={{ width: "100px" }}
            />
          </div>
        </form>
      </div>
    </div>
  );
});

function SectionHeader({ label }) {
  return (
    <Row>
      <div className="d-flex w-100">
        <h4 className="ml-2">{label || ""}</h4>
      </div>
      <Divider align="top" type="solid" />
    </Row>
  );
}

SectionHeader.propTypes = {
  label: PropTypes.string,
};

Reporting.propTypes = {
  generateReport: PropTypes.func.isRequired,
  customers: PropTypes.array,
  fetchFuelCustomers: PropTypes.func,
  isGenerateReportSuccess: PropTypes.bool,
};

export default Reporting;
